import { useEffect, useState, useCallback, useRef } from 'react';
import { api, isApiSessionBlocked } from '@ohif/core';

const PUBLIC_CHECK = 'https://www.google.com/generate_204';

export function useNetworkStability() {
  const [status, setStatus] = useState({
    isOnline: navigator.onLine,
    isApiReachable: true,
    latency: null,
    stability: 'stable',
  });

  const timerRef = useRef(null);

  const checkConnectivity = useCallback(async () => {
    // A denied/expired session already blocks the api instance; don't keep polling forever.
    if (isApiSessionBlocked()) {
      return;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 4000);
    const startTime = performance.now();

    try {
      // The api instance's baseURL is our backend; requesting '/healtz' preserves
      // the original ${API_URL}/healtz target through the centralized axios instance.
      const response = await api.head('/healtz', {
        signal: controller.signal,
      });

      const latency = performance.now() - startTime;
      const isOk = response.status >= 200 && response.status < 300;

      setStatus({
        isOnline: true,
        isApiReachable: isOk,
        latency,
        stability: isOk && latency < 800 ? 'stable' : 'unstable',
      });

      scheduleNext(30000);
    } catch {
      const hasInternet = await verifyInternet();

      setStatus({
        isOnline: hasInternet,
        isApiReachable: false,
        latency: null,
        stability: 'down',
      });

      scheduleNext(5000);
    } finally {
      clearTimeout(timeoutId);
    }
  }, []);

  const verifyInternet = async () => {
    if (!navigator.onLine) {
      return false;
    }

    // Uses fetch's no-cors mode intentionally: axios/XHR cannot make opaque
    // cross-origin requests, so this reachability probe can't be migrated.
    try {
      await fetch(PUBLIC_CHECK, { mode: 'no-cors', method: 'GET', cache: 'no-store' });
      return true;
    } catch {
      return false;
    }
  };

  const scheduleNext = ms => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(checkConnectivity, ms);
  };

  useEffect(() => {
    checkConnectivity();
    window.addEventListener('online', checkConnectivity);
    window.addEventListener('offline', checkConnectivity);

    return () => {
      window.removeEventListener('online', checkConnectivity);
      window.removeEventListener('offline', checkConnectivity);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [checkConnectivity]);

  return status;
}
